import { ThirdPartySource } from './audience-guesstimate.utils';
import type { DpReachResult } from './data-platform-audience-reach.service';
import {
  DataPlatformAudienceCountManyRequest,
  DataPlatformAudienceDefinition,
  DataPlatformAudienceRule,
  DataPlatformAudienceSizeEstimate,
  DataPlatformAudienceUnionMember,
} from '../data-platform.model';
import { EstimateFactor } from './reach-estimate-factors.utils';

/** DataPlatform's cap on union members in one batched count. */
export const UNIQUE_REACH_MAX_POOLS = 100;

export interface ReachPool {
  /** Null for everyone on the site. */
  rules: DataPlatformAudienceRule[] | null;
  /** Omitted when the reach is the whole pool. */
  estimate?: DataPlatformAudienceSizeEstimate;
  /** Shared by pools narrowed by the same estimated criteria. */
  group?: string;
}

export interface UniqueReachQuery {
  request: DataPlatformAudienceCountManyRequest;
  estimated: boolean;
}

/** A ceiling counts in full except at the low end. `wholePool`: the value is the pool's own count. */
export function reachPools(
  reach: Pick<DpReachResult, 'kind' | 'value' | 'range'>,
  rules: DataPlatformAudienceRule[] | null,
  group: string | undefined,
  wholePool: boolean,
): ReachPool[] | undefined {
  const value = reach.value;
  if (value == null || !['exact', 'estimate', 'upper-bound'].includes(reach.kind)) {
    return undefined;
  }
  if (reach.kind === 'exact' && wholePool) {
    return [{ rules }];
  }
  const estimate =
    reach.kind === 'upper-bound'
      ? { low: 0, centre: value, high: value }
      : {
          low: Math.min(reach.range?.lower ?? value, value),
          centre: value,
          high: Math.max(reach.range?.upper ?? value, value),
        };
  return [{ rules, estimate, ...(group && { group }) }];
}

export function estimateGroup(
  factors: readonly EstimateFactor[],
  source?: ThirdPartySource | null,
): string | undefined {
  const keys = factors.map(
    (factor) =>
      `${factor.dimension}:${factor.negate ? 'not:' : ''}${factor.matchMode ?? 'exact'}:${[...factor.bucketKeys].sort().join(',')}`,
  );
  if (source) {
    keys.push(`list:${source.negated ? 'not:' : ''}${source.app}:${source.listId}`);
  }
  return keys.length ? keys.sort().join('&') : undefined;
}

/** Keeps the largest reaches' pools when there are more than the cap. Null when no reach has a pool. */
export function buildUniqueReachQuery(
  siteId: number,
  reaches: readonly Pick<DpReachResult, 'value' | 'pools'>[],
): UniqueReachQuery | null {
  const pools = [...reaches]
    .sort((one, other) => (other.value ?? 0) - (one.value ?? 0))
    .flatMap((reach) => reach.pools ?? [])
    .slice(0, UNIQUE_REACH_MAX_POOLS);
  if (pools.length === 0) {
    return null;
  }

  const audiences: DataPlatformAudienceDefinition[] = [];
  const audienceIds = new Map<string, string>();
  const audienceIdOf = (rules: DataPlatformAudienceRule[]): string => {
    const key = JSON.stringify(rules);
    let id = audienceIds.get(key);
    if (!id) {
      id = `pool-${audiences.length}`;
      audienceIds.set(key, id);
      audiences.push({ id, rules });
    }
    return id;
  };
  const union = pools.map((pool): DataPlatformAudienceUnionMember => ({
    ...(pool.rules && { audienceId: audienceIdOf(pool.rules) }),
    ...(pool.estimate && { estimate: pool.estimate }),
    ...(pool.group && { group: pool.group }),
  }));
  if (audiences.length === 0) {
    // DataPlatform requires at least one audience.
    audiences.push({ id: 'pool-identified', rules: [{ type: 'is_identified' }] });
  }
  return {
    request: { siteIds: [siteId], audiences, union },
    estimated: union.some((member) => !!member.estimate),
  };
}
